import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export function useCoupons(filters = {}) {
  const [coupons, setCoupons]   = useState([]);
  const [meta, setMeta]         = useState({ total: 0, last_page: 1, current_page: 1 });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const fetchCoupons = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/coupons', { params: { ...filters, ...params } });
      setCoupons(data?.data ?? []);
      setMeta(data?.meta ?? { total: 0, last_page: 1, current_page: 1 });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load coupons');
    } finally { setLoading(false); }
  }, [JSON.stringify(filters)]); // eslint-disable-line

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  return { coupons, meta, loading, error, refetch: fetchCoupons };
}

export function useFeaturedCoupons(limit = 8) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/coupons/featured', { params: { limit } })
      .then(({ data }) => setCoupons(data?.data ?? []))
      .catch(() => setCoupons([]))
      .finally(() => setLoading(false));
  }, [limit]);

  return { coupons, loading };
}

export function useCoupon(id) {
  const [coupon, setCoupon]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/coupons/${id}`)
      .then(({ data }) => setCoupon(data.data))
      .catch(() => setError('Coupon not found'))
      .finally(() => setLoading(false));
  }, [id]);

  return { coupon, loading, error };
}

export function useRecommendedCoupons(limit = 12) {
  const [coupons, setCoupons]   = useState([]);
  const [personalized, setPers] = useState(false);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.get('/coupons/recommended', { params: { limit } })
      .then(({ data }) => {
        setCoupons(data?.data ?? []);
        setPers(!!data?.personalized);
      })
      .catch(() => setCoupons([]))
      .finally(() => setLoading(false));
  }, [limit]);

  return { coupons, personalized, loading };
}

export function useSimilarCoupons(couponId, limit = 6) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!couponId) { setLoading(false); return; }
    setLoading(true);
    api.get(`/coupons/${couponId}/similar`, { params: { limit } })
      .then(({ data }) => setCoupons(data?.data ?? []))
      .catch(() => setCoupons([]))
      .finally(() => setLoading(false));
  }, [couponId, limit]);

  return { coupons, loading };
}

export function useCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    api.get('/categories')
      .then(({ data }) => setCategories(data?.data ?? []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  return { categories, loading };
}

// Public banners for a given placement (e.g. 'homepage_top'). The
// admin sees a separate `useBanners` that hits /admin/banners; this is
// the visitor-facing version that the marketing slots consume. We
// silently swallow errors so a banner-service hiccup never blocks the
// rest of the page from rendering.
export function usePublicBanners(placement) {
  const [banners, setBanners] = useState([]);
  useEffect(() => {
    if (!placement) { setBanners([]); return; }
    api.get('/banners', { params: { placement } })
      .then(({ data }) => setBanners(data?.data ?? []))
      .catch(() => setBanners([]));
  }, [placement]);
  return banners;
}
